/**
 * Narration data catalog — Phase 25-AB Task 13.
 *
 * Source-of-truth: docs/mockups/narration.html (panels 24-C, 24-D, 25-A).
 *
 * Mockup defines:
 *   - 12 story themes (documentary / storytelling / tutorial / review /
 *     news / interview / vlog / ad / cinematic / teaser / comedy /
 *     commentary)
 *   - 12 tones (cheerful / calm / sad / excited / serious / tense /
 *     warm / mysterious / assertive / gentle / angry / thrilled)
 *   - 5 preset voices (민준 / 지우 / 혜진 / 태양 / 수진) + 1 Clone slot
 *
 * No emoji (D-2202). Tone of icon mapping is owned by ToneGrid.tsx —
 * this file is data only.
 */

export interface StoryTheme {
	readonly key: string;
	readonly name: string;
	readonly tag: string;
	readonly description: string;
	/** Unicode shape glyph (NOT emoji, per D-2202). */
	readonly glyph: string;
}

export const STORY_THEMES: readonly StoryTheme[] = [
	{
		key: "documentary",
		name: "다큐멘터리",
		tag: "documentary",
		description: "심도 있는 분석 · 정보 중심 톤",
		glyph: "▤",
	},
	{
		key: "storytelling",
		name: "스토리텔링",
		tag: "storytelling",
		description: "전개 · 갈등 · 해결 구조",
		glyph: "▦",
	},
	{
		key: "tutorial",
		name: "튜토리얼",
		tag: "tutorial",
		description: "단계별 가르치는 톤",
		glyph: "◐",
	},
	{
		key: "review",
		name: "리뷰",
		tag: "review",
		description: "장단점 비교 평가",
		glyph: "★",
	},
	{
		key: "news",
		name: "뉴스",
		tag: "news",
		description: "보도 형식 객관 톤",
		glyph: "▥",
	},
	{
		key: "interview",
		name: "인터뷰",
		tag: "interview",
		description: "질문 · 답변 자연스러운 흐름",
		glyph: "◉",
	},
	{
		key: "vlog",
		name: "브이로그",
		tag: "vlog",
		description: "친구처럼 캐주얼한 톤",
		glyph: "◯",
	},
	{
		key: "ad",
		name: "광고",
		tag: "ad",
		description: "구매 유도 설득 톤",
		glyph: "⊕",
	},
	{
		key: "cinematic",
		name: "시네마틱",
		tag: "cinematic",
		description: "영화적 분위기 · 몰입감",
		glyph: "◈",
	},
	{
		key: "teaser",
		name: "티저",
		tag: "teaser",
		description: "후킹 · 궁금증 자극",
		glyph: "▶",
	},
	{
		key: "comedy",
		name: "코미디",
		tag: "comedy",
		description: "유머 · 반전 활용",
		glyph: "▢",
	},
	{
		key: "commentary",
		name: "시사·논평",
		tag: "commentary",
		description: "사회 이슈 분석 · 의견 제시",
		glyph: "◑",
	},
] as const;

export const STORY_THEME_COUNT = STORY_THEMES.length;

export interface Tone {
	readonly key: string;
	readonly name: string;
	readonly en: string;
	readonly glyph: string;
}

export const TONES: readonly Tone[] = [
	{ key: "cheerful", name: "밝게", en: "cheerful", glyph: "↑" },
	{ key: "calm", name: "차분", en: "calm", glyph: "~" },
	{ key: "sad", name: "슬프게", en: "sad", glyph: "↓" },
	{ key: "excited", name: "신나게", en: "excited", glyph: "★" },
	{ key: "serious", name: "진중하게", en: "serious", glyph: "◯" },
	{ key: "tense", name: "긴장감", en: "tense", glyph: "⚡" },
	{ key: "warm", name: "따뜻하게", en: "warm", glyph: "◉" },
	{ key: "mysterious", name: "신비롭게", en: "mysterious", glyph: "◐" },
	{ key: "assertive", name: "단호하게", en: "assertive", glyph: "■" },
	{ key: "gentle", name: "부드럽게", en: "gentle", glyph: "○" },
	{ key: "angry", name: "분노", en: "angry", glyph: "▲" },
	{ key: "thrilled", name: "흥분", en: "thrilled", glyph: "✦" },
] as const;

export const TONE_COUNT = TONES.length;

export type VoiceGender = "female" | "male";

export interface Voice {
	readonly key: string;
	readonly name: string;
	readonly gender: VoiceGender;
	readonly language: string;
	readonly description: string;
	readonly initial: string;
	/** 0–4 inclusive — rendered as the mockup's ★ rating. */
	readonly rating: number;
	/** Visual style key for avatar gradient (matches mockup .voice-avatar.*). */
	readonly avatarStyle: "m1" | "f1" | "f2" | "m2" | "f3";
}

export const VOICES: readonly Voice[] = [
	{
		key: "minjun",
		name: "민준",
		gender: "male",
		language: "ko-KR",
		description: "신뢰감 / 차분 · 다큐 · 뉴스",
		initial: "민",
		rating: 2,
		avatarStyle: "m1",
	},
	{
		key: "jiwoo",
		name: "지우",
		gender: "female",
		language: "ko-KR",
		description: "밝은 / 친근 · vlog · 리뷰",
		initial: "지",
		rating: 3,
		avatarStyle: "f1",
	},
	{
		key: "hyejin",
		name: "혜진",
		gender: "female",
		language: "ko-KR",
		description: "감성 / 깊은 · 스토리 · 영화",
		initial: "혜",
		rating: 4,
		avatarStyle: "f2",
	},
	{
		key: "taeyang",
		name: "태양",
		gender: "male",
		language: "ko-KR",
		description: "에너지 / 강렬 · 게임 · 스포츠",
		initial: "태",
		rating: 2,
		avatarStyle: "m2",
	},
	{
		key: "sujin",
		name: "수진",
		gender: "female",
		language: "ko-KR",
		description: "차분 / 명확 · 교육 · 튜토",
		initial: "수",
		rating: 2,
		avatarStyle: "f3",
	},
] as const;

export const VOICE_COUNT = VOICES.length;

/** Clone slot key — used in VoiceList to render the dashed "my voice" row. */
export const VOICE_CLONE_KEY = "clone";

export type NarrationMode = "A" | "B";
export type NarrationSubmode = "A-1" | "A-2" | null;
